// === ÉVÉNEMENTS NARRATIFS ===
// Rencontres aléatoires sur la carte. Chaque event propose 2-3 choix.

export interface EventChoice {
  label: string;
  emoji: string;
  /** Effet appliqué au joueur */
  effect: EventEffect;
}

export interface EventEffect {
  hp?: number;        // +/- HP
  gold?: number;      // +/- or
  item?: string;      // ID d'item à donner
  removeItem?: boolean; // retirer un item aléatoire
  resources?: Partial<Record<'wood' | 'iron' | 'cloth' | 'crystal' | 'cursed_bone', number>>;
}

export interface GameEvent {
  id: string;
  title: string;
  emoji: string;
  description: string;
  choices: EventChoice[];
}

export const EVENTS: GameEvent[] = [
  {
    id: 'shipwreck',
    title: 'Épave échouée',
    emoji: '🚢',
    description: 'Une carcasse de navire gît sur le rivage. Des caisses semblent intactes, mais la structure craque dangereusement.',
    choices: [
      { label: 'Fouiller les caisses', emoji: '🔍', effect: { gold: 15, hp: -3 } },
      { label: 'Récupérer le bois', emoji: '🪵', effect: { gold: 8, resources: { wood: 3, iron: 1 } } },
      { label: 'Passer son chemin', emoji: '🚶', effect: {} },
    ],
  },
  {
    id: 'old_pirate',
    title: 'Vieux pirate',
    emoji: '🧓',
    description: 'Un vieux loup de mer est assis près d\'un feu. Il propose un marché : un secret contre un peu de rhum (or).',
    choices: [
      { label: 'Payer 10 or', emoji: '💰', effect: { gold: -10, hp: 8 } },
      { label: 'Le menacer', emoji: '⚔️', effect: { gold: 5, hp: -5 } },
      { label: 'Discuter amicalement', emoji: '🗣️', effect: { hp: 3 } },
    ],
  },
  {
    id: 'cursed_chest',
    title: 'Coffre maudit',
    emoji: '📦',
    description: 'Un coffre orne d\'os humains brille d\'une lueur violette. Il semble plein... mais à quel prix ?',
    choices: [
      { label: 'Ouvrir le coffre', emoji: '🔓', effect: { gold: 25, hp: -8 } },
      { label: 'Briser la malédiction', emoji: '✨', effect: { gold: 12, resources: { crystal: 1 } } },
      { label: 'Le laisser tranquille', emoji: '🚶', effect: {} },
    ],
  },
  {
    id: 'wounded_sailor',
    title: 'Marin blessé',
    emoji: '🩹',
    description: 'Un marin gémit au bord du chemin. Il tend une bourse en échange de soins.',
    choices: [
      { label: 'L\'aider (perdre 5 HP)', emoji: '❤️', effect: { hp: -5, gold: 20 } },
      { label: 'Le dépouiller', emoji: '💀', effect: { gold: 12, hp: -2 } },
      { label: 'Passer', emoji: '🚶', effect: {} },
    ],
  },
  {
    id: 'mysterious_fountain',
    title: 'Fontaine mystérieuse',
    emoji: '⛲',
    description: 'Une fontaine de pierre couverte de mousse coule encore. L\'eau a une teinte étrange...',
    choices: [
      { label: 'Boire l\'eau', emoji: '🥤', effect: { hp: 10 } },
      { label: 'Jeter une pièce', emoji: '🪙', effect: { gold: -5, hp: 15 } },
      { label: 'Ne rien faire', emoji: '🚶', effect: {} },
    ],
  },
  {
    id: 'gambling_ghost',
    title: 'Fantôme joueur',
    emoji: '👻',
    description: 'Un spectre translucide vous défie aux dés. "Tout ou rien, moussaillon !"',
    choices: [
      { label: 'Jouer (50/50)', emoji: '🎲', effect: { gold: 20 } }, // le 50/50 sera géré dans la scène
      { label: 'Refuser poliment', emoji: '🙅', effect: { gold: 5 } },
    ],
  },
  {
    id: 'abandoned_camp',
    title: 'Camp abandonné',
    emoji: '⛺',
    description: 'Un campement vide mais encore tiède. Quelqu\'un est parti précipitamment, laissant des provisions.',
    choices: [
      { label: 'Fouiller le camp', emoji: '🔍', effect: { gold: 10, hp: 5, resources: { cloth: 2, iron: 1 } } },
      { label: 'Se reposer', emoji: '😴', effect: { hp: 8 } },
      { label: 'Tendre une embuscade', emoji: '🪤', effect: { gold: 18, hp: -4 } },
    ],
  },
  {
    id: 'voodoo_altar',
    title: 'Autel vaudou',
    emoji: '🕯️',
    description: 'Des bougies noires entourent un crâne gravé de runes. Une offrande pourrait attirer la faveur des esprits.',
    choices: [
      { label: 'Offrir 15 or', emoji: '💰', effect: { gold: -15, hp: 20 } },
      { label: 'Prier', emoji: '🙏', effect: { hp: 5 } },
      { label: 'Détruire l\'autel', emoji: '🔨', effect: { gold: 8, hp: -6, resources: { cursed_bone: 1 } } },
    ],
  },
  {
    id: 'treasure_map',
    title: 'Carte au trésor',
    emoji: '🗺️',
    description: 'Un parchemin délavé montre un X pas loin d\'ici. Ça sent le piège... ou la fortune.',
    choices: [
      { label: 'Suivre la carte', emoji: '🏴‍☠️', effect: { gold: 30, hp: -5, resources: { iron: 2, crystal: 1 } } },
      { label: 'Vendre le parchemin', emoji: '📜', effect: { gold: 10 } },
      { label: 'Ignorer', emoji: '🚶', effect: {} },
    ],
  },
  {
    id: 'sea_witch',
    title: 'Sorcière des mers',
    emoji: '🧙‍♀️',
    description: 'Une femme aux yeux blancs émerge de la brume. "Je peux soigner tes blessures... si tu acceptes mon prix."',
    choices: [
      { label: 'Accepter (payer 20 or)', emoji: '💰', effect: { gold: -20, hp: 30 } },
      { label: 'Marchander', emoji: '🤝', effect: { gold: -8, hp: 12 } },
      { label: 'Fuir', emoji: '🏃', effect: { hp: -3 } },
    ],
  },
];
